import { iconArrowRight } from "assets/icons";
import { Button } from "components/Button";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import Papa from "papaparse";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { BaseRoutes } from "routes/BaseRoutes";
import {
  STATUS_LABELS,
  formatCentsCsv,
  formatCpf,
  formatDateIso,
  formatDateTime,
  formatDateTimeCsv,
  orEmpty,
} from "../formatters";

interface TicketIngestion {
  ticketIngestionId: string;
  eventSlug: string;
  externalTicketId: string;
  source?: string | null;
  status: string;
  holderName: string;
  cpf?: string | null;
  email?: string | null;
  passName?: string | null;
  insertDate: string;
}

interface Property {
  name: string;
  value: string;
}

const LIST_QUERY = `
  query BackofficeTicketIngestions(
    $filter: TicketIngestionFilterInput
    $pagination: PaginationInput
  ) {
    backofficeTicketIngestions(filter: $filter, pagination: $pagination) {
      items {
        ticketIngestionId
        eventSlug
        externalTicketId
        source
        status
        holderName
        cpf
        email
        passName
        insertDate
      }
      pagination {
        totalCount
      }
    }
  }
`;

interface TicketIngestionExport extends TicketIngestion {
  externalOrderId?: string | null;
  phone?: string | null;
  company?: string | null;
  position?: string | null;
  category?: string | null;
  purchasedAt?: string | null;
  amountPaidCents?: number | null;
  paymentMethod?: string | null;
  processed: boolean;
  registrationId?: string | null;
  errorMessage?: string | null;
}

const EXPORT_QUERY = `
  query BackofficeTicketIngestionsExport($filter: TicketIngestionFilterInput) {
    backofficeTicketIngestionsExport(filter: $filter) {
      holderName
      cpf
      email
      phone
      company
      position
      passName
      category
      status
      eventSlug
      source
      externalTicketId
      externalOrderId
      amountPaidCents
      paymentMethod
      purchasedAt
      insertDate
      processed
      registrationId
      errorMessage
    }
  }
`;

// Mensagem do api-v4 quando o recorte passa do teto da exportação; o toast
// do connectGraphQLApi já a mostra e não deve ser trocado pelo genérico.
const EXPORT_LIMIT_ERROR_PREFIX = "Muitos ingressos para exportar";

const toCsvRow = (ticket: TicketIngestionExport) => ({
  Nome: ticket.holderName ?? "",
  CPF: ticket.cpf ?? "",
  "E-mail": ticket.email ?? "",
  Telefone: ticket.phone ?? "",
  Empresa: ticket.company ?? "",
  Cargo: ticket.position ?? "",
  Ingresso: ticket.passName ?? "",
  Categoria: ticket.category ?? "",
  Status: STATUS_LABELS[ticket.status] ?? ticket.status ?? "",
  Evento: ticket.eventSlug ?? "",
  Origem: ticket.source ?? "",
  "Id do ingresso": ticket.externalTicketId ?? "",
  "Id do pedido": ticket.externalOrderId ?? "",
  "Valor pago": formatCentsCsv(ticket.amountPaidCents),
  "Forma de pagamento": ticket.paymentMethod ?? "",
  "Comprado em": formatDateTimeCsv(ticket.purchasedAt),
  "Recebido em": formatDateTimeCsv(ticket.insertDate),
  Processado: ticket.processed ? "Sim" : "Não",
  "Id da inscrição": ticket.registrationId ?? "",
  "Erro de processamento": ticket.errorMessage ?? "",
});

const EVENT_SLUGS_QUERY = `
  query BackofficeTicketIngestionEventSlugs {
    backofficeTicketIngestionEventSlugs
  }
`;

const initialTableFilters: FilterProps = {
  eventSlug: {
    label: "Evento",
    value: "",
    options: [],
  },
  status: {
    label: "Status",
    value: "",
    options: Object.entries(STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  },
};

export const useTicketIngestionsTable = () => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  const { push } = useHistory();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [tickets, setTickets] = useState<TicketIngestion[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableFilters, setTableFilters] =
    useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);
  const searchTextFormRef = useRef<any>(null);

  const detailsRoute = (ticketIngestionId: string) =>
    BaseRoutes.ticketIngestionDetails.replace(":id", ticketIngestionId);

  /* HOOKS */
  useEffect(() => {
    connectGraphQLApi({
      query: EVENT_SLUGS_QUERY,
      errorMessage: "Falha ao listar os eventos",
      useNewApi: true,
    })
      .then((response: any) => {
        const slugs: string[] =
          response.data.backofficeTicketIngestionEventSlugs || [];
        setTableFilters((filters) => ({
          ...filters,
          eventSlug: {
            ...filters.eventSlug,
            options: slugs.map((slug) => ({ value: slug, label: slug })),
          },
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPageNumber(1);
  }, [tableFilters.eventSlug.value, tableFilters.status.value, searchText, rowsPerPage]);

  useEffect(() => {
    getTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilters.eventSlug.value, tableFilters.status.value, searchText, pageNumber, rowsPerPage]);

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    setSearchText(event.target.searchText.value.trim());
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const { name, value } = event.target;
    setTableFilters({
      ...tableFilters,
      [name]: { ...tableFilters[name], value },
    });
  };

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters({
      ...tableFilters,
      [filterName]: { ...tableFilters[filterName], value: "" },
    });
  };

  const handleSearchTextRemove = (): void => {
    searchTextFormRef.current?.reset();
    setSearchText("");
  };

  const handleClearFilters = (): void => {
    setTableFilters((filters) => ({
      eventSlug: { ...filters.eventSlug, value: "" },
      status: { ...filters.status, value: "" },
    }));
    handleSearchTextRemove();
  };

  const handlePageSelected = (page: number): void => {
    setPageNumber(page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    setRowsPerPage(parseInt(event.target.value));
  };

  const handleRowClick = (_row: any, rowIndex: number): void => {
    const ticket = tickets[rowIndex];
    if (ticket) push(detailsRoute(ticket.ticketIngestionId));
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const eventSlug = tableFilters.eventSlug.value;
    const status = tableFilters.status.value;

    try {
      const response: any = await connectGraphQLApi({
        query: EXPORT_QUERY,
        variables: {
          filter: {
            eventSlug: eventSlug || null,
            status: status || null,
            search: searchText || null,
          },
        },
        errorMessage: "Erro ao exportar ingressos.",
        useNewApi: true,
      });

      const tickets: TicketIngestionExport[] =
        response?.data?.backofficeTicketIngestionsExport || [];

      if (tickets.length === 0) {
        setToast({
          type: "warning",
          message: "Não há ingressos para exportar.",
          isActive: true,
          timeToRemoveToast: 4000,
        });
        return;
      }

      const csv = Papa.unparse(tickets.map(toCsvRow));
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = [
        "ingressos-parceiros",
        eventSlug || "todos",
        status,
        formatDateIso(new Date()),
      ]
        .filter(Boolean)
        .join("-")
        .concat(".csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setToast({
        type: "success",
        message: "Ingressos exportados com sucesso!",
        isActive: true,
        timeToRemoveToast: 4000,
      });
    } catch (error: any) {
      console.error("[handleExportCsv]", error);
      const isLimitError =
        typeof error?.message === "string" &&
        error.message.startsWith(EXPORT_LIMIT_ERROR_PREFIX);
      if (error?.errorType !== "auth" && !isLimitError) {
        setToast({
          type: "error",
          message: "Erro ao exportar ingressos.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      }
    } finally {
      setExporting(false);
    }
  };

  /* API CALLS */
  const getTickets = async () => {
    setLoading(true);
    try {
      const response: any = await connectGraphQLApi({
        query: LIST_QUERY,
        variables: {
          filter: {
            eventSlug: tableFilters.eventSlug.value || null,
            status: tableFilters.status.value || null,
            search: searchText || null,
          },
          pagination: {
            offset: (pageNumber - 1) * rowsPerPage,
            limit: rowsPerPage,
          },
        },
        errorMessage: "Falha ao listar os ingressos",
        useNewApi: true,
      });

      const { items, pagination } = response.data.backofficeTicketIngestions;
      setTickets(items);
      setTotalCount(pagination.totalCount);
      setProperties([
        { name: "Ingressos encontrados", value: pagination.totalCount.toString() },
      ]);
      setError(false);
    } catch (error: any) {
      console.error("Error details:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const ticketRows = tickets.map((ticket) => ({
    holderName: ticket.holderName,
    cpf: formatCpf(ticket.cpf),
    email: orEmpty(ticket.email),
    passName: orEmpty(ticket.passName),
    status: STATUS_LABELS[ticket.status] ?? ticket.status,
    eventSlug: ticket.eventSlug,
    source: orEmpty(ticket.source),
    insertDate: formatDateTime(ticket.insertDate),
    actions: [
      <Button
        key="details"
        label=""
        size="small"
        icon={iconArrowRight}
        onlyIcon={true}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          push(detailsRoute(ticket.ticketIngestionId));
        }}
      />,
    ],
  }));

  return {
    loading,
    error,
    properties,
    ticketRows,
    totalCount,
    tableFilters,
    searchText,
    searchTextFormRef,
    pageNumber,
    rowsPerPage,
    exporting,
    handleExportCsv,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handleClearFilters,
    handlePageSelected,
    handleRowsPerPageChange,
    handleRowClick,
  };
};
